import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { InfoCircleLine, Pencil, PersonNegative, Plus } from '@factorialco/f0-react/icons/app'
import {
  SalaryAdvanceEmployeeProfileFragment,
  SalaryAdvanceEmployeeProfilesCollectionDocument,
  SalaryAdvanceEmployeeProfilesCollectionQuery,
  SalaryAdvanceValueObjectsEmployeeStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import {
  getEmployeeStatusLabel,
  getEmployeeStatusVariant,
} from 'modules/salary_advance/lib/employeeHelpers'
import { AccrualBreakdownSidePanel } from 'modules/salary_advance/pages/EmployeesPage/AccrualBreakdownSidePanel'
import { ToggleStatusModal } from 'modules/salary_advance/pages/EmployeesPage/EmployeesTable/ToggleStatusModal'
import { MissingInfoSidePanel } from 'modules/salary_advance/pages/EmployeesPage/MissingInfoSidePanel'

type EmployeeStatusAction = 'enable' | 'disable'

interface SelectedEmployeesForStatusChange {
  employeeIds: string[]
  action: EmployeeStatusAction
}
const DEFAULT_PER_PAGE = 25

export const EmployeesTable = () => {
  const { access } = useCurrentSession()
  const locale = access.locale || 'en'

  const [sidepanelOpen, setSidepanelOpen] = useState(false)
  const [selectedEmployeesForEdit, setSelectedEmployeesForEdit] = useState<
    SalaryAdvanceEmployeeProfileFragment[]
  >([])
  const [breakdownSidepanelOpen, setBreakdownSidepanelOpen] = useState(false)
  const [selectedEmployeeForBreakdown, setSelectedEmployeeForBreakdown] =
    useState<SalaryAdvanceEmployeeProfileFragment | null>(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [selectedEmployeesForStatusChange, setSelectedEmployeesForStatusChange] =
    useState<SelectedEmployeesForStatusChange | null>(null)

  const handleOpenStatusModal = (employeeIds: string[], action: EmployeeStatusAction) => {
    if (employeeIds.length === 0) return

    setSelectedEmployeesForStatusChange({
      employeeIds,
      action,
    })
    setStatusModalOpen(true)
  }

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false)
    setSelectedEmployeesForStatusChange(null)
  }

  const emptyState = {
    'no-data': {
      title: i18n.t('salary_advance.employee.table.empty_state.title'),
      description: i18n.t('salary_advance.employee.table.empty_state.description'),
    },
  }

  const employeeStatusOptions: Array<{
    label: string
    value: SalaryAdvanceValueObjectsEmployeeStatusEnum
  }> = [
    {
      label: getEmployeeStatusLabel('pending_information'),
      value: 'pending_information',
    },
    {
      label: getEmployeeStatusLabel('active'),
      value: 'active',
    },
    {
      label: getEmployeeStatusLabel('disabled'),
      value: 'disabled',
    },
  ]

  const source = useGraphqlDataCollectionSource(
    {
      document: SalaryAdvanceEmployeeProfilesCollectionDocument,
      fetchPolicy: 'cache-and-network' as const,
      perPage: DEFAULT_PER_PAGE,
      mapVariables: ({ filters, search }) => {
        return {
          status: filters?.status,
          search,
        }
      },
      search: {
        enabled: true,
      },
      totalItemSummary: (totalItems) => {
        if (totalItems === 0) {
          return i18n.t('salary_advance.employee.table.zero_items')
        }
        return i18n.tp('salary_advance.employee.table.total_items', {
          count: totalItems,
        })
      },
      presets: [
        {
          label: i18n.t('common.all'),
          filter: { status: undefined },
        },
        {
          label: i18n.t('salary_advance.employee.status.pending_information'),
          filter: { status: ['pending_information'] },
        },
        {
          label: i18n.t('salary_advance.employee.status.active'),
          filter: { status: ['active'] },
        },
        {
          label: i18n.t('salary_advance.employee.status.disabled'),
          filter: { status: ['disabled'] },
        },
      ],
      filters: {
        status: {
          type: 'in',
          label: i18n.t('common.status'),
          options: {
            options: employeeStatusOptions,
          },
        },
      },
      mapData: (data: SalaryAdvanceEmployeeProfilesCollectionQuery) => {
        return data.salaryAdvance.employeeProfilesConnection.edges.map((edge) => edge.node)
      },
      mapPagination: (data: SalaryAdvanceEmployeeProfilesCollectionQuery) => {
        const count = data.salaryAdvance.employeeProfilesConnection.totalCount ?? 0
        return { total: count }
      },
      selectable: (item) => item.employeeId,
      bulkActions: (selectedRows) => {
        const selectedEmployees = selectedRows.itemsStatus.filter((item) => item.checked)

        if (selectedEmployees.length === 0) {
          return {}
        }

        const allPendingInformation = selectedEmployees.every(
          (item) => item.item.status === 'pending_information'
        )
        const allDisabled = selectedEmployees.every((item) => item.item.status === 'disabled')
        const allActive = selectedEmployees.every((item) => item.item.status !== 'disabled')

        // For pending_information: primary = edit, secondary = deactivate
        if (allPendingInformation) {
          return {
            primary: [
              {
                id: 'edit_employees',
                label: i18n.t('employees.employees_list.actions.edit_employees'),
                icon: Pencil,
              },
            ],
            secondary: [
              {
                id: 'disable_employees',
                label: i18n.t('salary_advance.employee.actions.bulk_disable'),
                icon: PersonNegative,
              },
            ],
          }
        }

        // For disabled: show enable action
        if (allDisabled) {
          return {
            primary: [
              {
                id: 'enable_employees',
                label: i18n.t('salary_advance.employee.actions.bulk_enable'),
                icon: Plus,
              },
            ],
          }
        }

        // For active (non-pending): show disable action
        if (allActive) {
          return {
            primary: [
              {
                id: 'disable_employees',
                label: i18n.t('salary_advance.employee.actions.bulk_disable'),
                icon: PersonNegative,
              },
            ],
          }
        }

        // Mixed statuses
        return {
          warningMessage: i18n.t('salary_advance.employee.bulk.mixed_status_warning'),
        }
      },
      itemActions: (item: SalaryAdvanceEmployeeProfileFragment) => {
        if (!item.employeeName) return []

        const employeeId = item.employeeId
        const actions = []

        // Add edit option for pending_information employees
        if (item.status === 'pending_information') {
          actions.push({
            label: i18n.t('employees.employees_list.actions.edit_employee'),
            icon: Pencil,
            onClick: () => {
              setSelectedEmployeesForEdit([item])
              setSidepanelOpen(true)
            },
          })
        }

        // Add accrual breakdown action for active employees
        if (item.status === 'active') {
          actions.push({
            label: i18n.t('salary_advance.employee.actions.view_breakdown'),
            icon: InfoCircleLine,
            onClick: () => {
              setSelectedEmployeeForBreakdown(item)
              setBreakdownSidepanelOpen(true)
            },
          })
        }

        // Add enable/disable based on status
        if (item.status === 'disabled') {
          actions.push({
            label: i18n.t('salary_advance.employee.actions.enable_employee'),
            icon: Plus,
            onClick: () => handleOpenStatusModal([employeeId], 'enable'),
          })
        } else {
          actions.push({
            label: i18n.t('salary_advance.employee.actions.disable_employee'),
            icon: PersonNegative,
            onClick: () => handleOpenStatusModal([employeeId], 'disable'),
          })
        }

        return actions
      },
    },
    []
  )

  const handleBulkAction = (
    action: string,
    {
      itemsStatus,
    }: {
      itemsStatus: readonly {
        item: SalaryAdvanceEmployeeProfileFragment
        checked: boolean
      }[]
    }
  ) => {
    const selectedEmployees = itemsStatus.filter((item) => item.checked).map((item) => item.item)
    const employeeIds = selectedEmployees.map((item) => item.employeeId)

    if (action === 'edit_employees') {
      setSelectedEmployeesForEdit(selectedEmployees)
      setSidepanelOpen(true)
    } else if (action === 'enable_employees') {
      handleOpenStatusModal(employeeIds, 'enable')
    } else if (action === 'disable_employees') {
      handleOpenStatusModal(employeeIds, 'disable')
    }
  }

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={handleBulkAction}
        emptyStates={emptyState}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('common.employee'),
                  render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                    if (!item.employeeName) {
                      return { type: 'text', value: '-' }
                    }

                    return {
                      type: 'person',
                      value: {
                        firstName: item.employeeName.firstName,
                        lastName: item.employeeName.lastName,
                        src: buildUrl(item.employeeName.avatar?.url ?? ''),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('common.status'),
                  render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                    if (item.status === 'pending_information') {
                      return {
                        type: 'alertTag' as const,
                        value: {
                          label: getEmployeeStatusLabel(item.status),
                          level: 'warning' as const,
                        },
                      }
                    }

                    return {
                      type: 'status',
                      value: {
                        label: getEmployeeStatusLabel(item.status),
                        status: getEmployeeStatusVariant(item.status),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('salary_advance.employee.table.columns.net_salary'),
                  info: i18n.t('salary_advance.employee.table.columns.net_salary_tooltip'),
                  render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                    return {
                      type: 'text',
                      value: item.netSalaryCents
                        ? MoneyWithCurrency({
                            amount: item.netSalaryCents,
                            currency: item.currency ?? 'EUR',
                            locale,
                          })
                        : '-',
                    }
                  },
                },
                {
                  label: i18n.t('salary_advance.employee.table.columns.monthly_maximum_accrual'),
                  render: (item: SalaryAdvanceEmployeeProfileFragment) => {
                    return {
                      type: 'text',
                      value: item.maxMonthlyAccrualCents
                        ? MoneyWithCurrency({
                            amount: item.maxMonthlyAccrualCents,
                            currency: item.currency ?? 'EUR',
                            locale,
                          })
                        : '-',
                    }
                  },
                },
              ],
            },
          },
        ]}
      />

      <MissingInfoSidePanel
        open={sidepanelOpen}
        onClose={() => setSidepanelOpen(false)}
        selectedEmployees={selectedEmployeesForEdit}
      />

      <AccrualBreakdownSidePanel
        open={breakdownSidepanelOpen}
        onClose={() => {
          setBreakdownSidepanelOpen(false)
          setSelectedEmployeeForBreakdown(null)
        }}
        employee={selectedEmployeeForBreakdown}
      />

      {selectedEmployeesForStatusChange && (
        <ToggleStatusModal
          open={statusModalOpen}
          onClose={handleCloseStatusModal}
          employeeIds={selectedEmployeesForStatusChange.employeeIds}
          action={selectedEmployeesForStatusChange.action}
        />
      )}
    </>
  )
}
